/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useContext, useState } from 'react';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import getAllNotifications from '../../../domain/use_cases/notifications/getAllNotifications';
import exportNotifications from '../../../domain/use_cases/notifications/exportNotifications';
import AugmentedNotifications from '../../../domain/models/@aggregates/AugmentedNotifications';
import AuthenticationContext from '../auth/AuthenticationContext';

export const NOTIFICATIONS_FETCH_LIMIT = 10; // NOTE : It can be 25

const NotificationsContext = createContext({
  count: 0,
  hitsPerPage: NOTIFICATIONS_FETCH_LIMIT,
  items: [] as AugmentedNotifications[],
  lastVisible: {} as QueryDocumentSnapshot<DocumentData, DocumentData>,
  scopedSubscriptionId: null as string | null,
  fetch: async (idToScopeOn: string | null, pendingOnly: boolean | false, limit?: number) => {},
  fetchNext: () => {},
  exportNotifs: async () => {},
  reset: () => {},
  status: ContextStatus.IDLE,
  exportStatus: ContextStatus.IDLE,
});

export const NotificationsProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [count, setCount] = useState(0);
  const [hitsPerPage, setHitsPerPage] = useState(NOTIFICATIONS_FETCH_LIMIT);
  const [items, setItems] = useState([] as AugmentedNotifications[]);
  const [lastVisible, setLastVisible] = useState(
    {} as QueryDocumentSnapshot<DocumentData, DocumentData>
  );
  const [scopedSubscriptionId, setScopedSubscriptionId] = useState(
    null as string | null
  );
  const [pendingOnly, setPendingOnly] = useState(false);
  const [status, setStatus] = useState(ContextStatus.IDLE);
  const [exportStatus, setExportStatus] = useState(ContextStatus.IDLE);

  const authContext = useContext(AuthenticationContext);

  const fetch = async (
    idToScopeOn: string | null,
    pendingOnly: boolean | false,
    limit?: number
  ) => {
    const effectiveLimit = limit ?? hitsPerPage;
    if (limit !== undefined) setHitsPerPage(limit);
    setScopedSubscriptionId(idToScopeOn);
    setPendingOnly(pendingOnly);
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getAllNotifications(
        effectiveLimit,
        undefined,
        idToScopeOn,
        pendingOnly
      );
      setItems(useCaseResponse.allNotifications);
      setLastVisible(useCaseResponse.lastVisible);
      setCount(useCaseResponse.totalCollectionCount);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchNext = async () => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getAllNotifications(
        hitsPerPage,
        lastVisible,
        scopedSubscriptionId,
        pendingOnly
      );
      setItems([...items, ...useCaseResponse.allNotifications]);
      setLastVisible(useCaseResponse.lastVisible);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const exportNotifs = async () => {
    if (authContext.authentication === null || scopedSubscriptionId === null) {
      return;
    }
    setExportStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await exportNotifications(
        authContext.authentication,
        scopedSubscriptionId
      );
      if (useCaseResponse.success) {
        setExportStatus(ContextStatus.SUCCESS);
      } else {
        setExportStatus(ContextStatus.ERROR);
      }
    } catch (e) {
      setExportStatus(ContextStatus.ERROR);
    }
  };

  const reset = () => {
    setCount(0);
    setHitsPerPage(NOTIFICATIONS_FETCH_LIMIT);
    setItems([]);
    setLastVisible({} as QueryDocumentSnapshot<DocumentData, DocumentData>);
    setScopedSubscriptionId(null);
    setStatus(ContextStatus.IDLE);
  };

  return (
    <NotificationsContext.Provider
      value={{
        count,
        hitsPerPage,
        items,
        lastVisible,
        scopedSubscriptionId,
        fetch,
        fetchNext,
        exportNotifs,
        reset,
        status,
        exportStatus,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export default NotificationsContext;
